import { pickLocalizedText, resolveLocalizedText } from '@houzy/schemas/offer-request-management';
import type { PartnerCockpitOfferRequestDetail } from '@houzy/schemas/partner-cockpit/offer-requests';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
    formatCreditsLabel,
    offerRequestJobCategories,
    offerRequestProjectSizeLabel,
    offerRequestLeadStatusLabel,
    offerRequestTimingLabel,
    offerRequestTitle,
} from '../labels';
import { formatOfferRequestSubtitle } from '../utils/formatOfferRequestSubtitle';
import { isImageAttachment } from '../utils/isImageAttachment';

export type OfferRequestDetailsCardProps = {
    offerRequest: PartnerCockpitOfferRequestDetail;
    // Forwarded test/clock anchor so stories and tests stay deterministic.
    now?: Date;
};

const DESCRIPTION_CLAMP_LINES = 3;

// Only a phone clamps, and only a description that really overflows earns the toggle — a media query
// cannot answer the second question, so the clamped element is measured while it is clamped.
function ProjectDescription({ text, inOriginalLanguage }: { text: string; inOriginalLanguage: boolean }): ReactElement {
    const { t } = useTranslation();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const isClamped = isMobile && !expanded;

    useLayoutEffect(() => {
        const element = textRef.current;
        if (!isClamped || element === null) {
            return;
        }
        const measure = (): void => setOverflows(element.scrollHeight > element.clientHeight + 1);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return (): void => observer.disconnect();
    }, [text, isClamped]);

    return (
        <Box>
            <Typography variant="subtitle1" component="h3" gutterBottom>
                {t(
                    inOriginalLanguage
                        ? 'OFFER_REQUESTS.DETAIL.PROJECT_DESCRIPTION_ORIGINAL_LANGUAGE'
                        : 'OFFER_REQUESTS.DETAIL.PROJECT_DESCRIPTION',
                )}
            </Typography>
            <Typography
                ref={textRef}
                variant="body1"
                sx={{
                    whiteSpace: 'pre-line',
                    ...(isClamped
                        ? {
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: DESCRIPTION_CLAMP_LINES,
                              overflow: 'hidden',
                          }
                        : {}),
                }}>
                {text}
            </Typography>
            {isMobile && overflows ? (
                <Link component="button" type="button" sx={{ mt: 1 }} onClick={() => setExpanded(!expanded)}>
                    {t(expanded ? 'OFFER_REQUESTS.DETAIL.SHOW_LESS' : 'OFFER_REQUESTS.DETAIL.SHOW_MORE')}
                </Link>
            ) : null}
        </Box>
    );
}

function Field({ label, value }: { label: string; value: string }): ReactElement {
    return (
        <Box>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                {value}
            </Typography>
        </Box>
    );
}

export function OfferRequestDetailsCard({ offerRequest, now }: OfferRequestDetailsCardProps): ReactElement {
    const { t, i18n } = useTranslation();

    const description = resolveLocalizedText(offerRequest.description, i18n.language);
    const documentAttachments = offerRequest.attachments.filter((attachment) => !isImageAttachment(attachment));
    const imageAttachments = offerRequest.attachments.filter(isImageAttachment);

    // Names why a lead reached by a stale link has no contact panel or actions left.
    const leadStatusBadge = offerRequestLeadStatusLabel(t, offerRequest.leadStatus);

    return (
        <Card variant="outlined">
            <CardHeader
                title={offerRequestTitle(
                    t,
                    offerRequest.title,
                    offerRequest.workCategoryKeys,
                    offerRequest.referenceId,
                    i18n.language,
                )}
                subheader={formatOfferRequestSubtitle(t, offerRequest, now)}
                slotProps={{
                    title: { variant: 'h6', component: 'h2', sx: { overflowWrap: 'anywhere' } },
                    subheader: { variant: 'body2' },
                }}
                action={leadStatusBadge === undefined ? undefined : <Chip size="small" label={leadStatusBadge} />}
                sx={{
                    // column-reverse lifts the status chip above the title on a phone, as drawn.
                    flexDirection: { xs: 'column-reverse', md: 'row' },
                    alignItems: 'flex-start',
                    gap: { xs: 1, md: 0 },
                    '& .MuiCardHeader-content': { alignSelf: 'stretch' },
                    '& .MuiCardHeader-action': { m: { xs: 0, md: '-4px -8px -4px 0' } },
                }}
            />
            <CardContent sx={{ pt: 0 }}>
                <Stack spacing={3}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={offerRequestTimingLabel(t, offerRequest.timeConstraint)} size="small" />
                        {offerRequest.credits !== undefined && (
                            <Chip label={formatCreditsLabel(t, offerRequest.credits)} size="small" />
                        )}
                    </Stack>

                    {description === undefined ? null : (
                        <ProjectDescription
                            text={description.text}
                            inOriginalLanguage={description.language !== i18n.language}
                        />
                    )}

                    <Divider />

                    <Stack spacing={2}>
                        <Field
                            label={t('OFFER_REQUESTS.DETAIL.DESIRED_TIME')}
                            value={offerRequestTimingLabel(t, offerRequest.timeConstraint)}
                        />
                        <Field
                            label={t('OFFER_REQUESTS.DETAIL.JOB_CATEGORIES')}
                            value={offerRequestJobCategories(t, offerRequest.workCategoryPaths)}
                        />
                        {offerRequest.projectSize !== undefined ? (
                            <Field
                                label={t('OFFER_REQUESTS.DETAIL.ESTIMATED_VALUE')}
                                value={offerRequestProjectSizeLabel(t, offerRequest.projectSize)}
                            />
                        ) : null}
                    </Stack>

                    {offerRequest.moreInformation.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" component="h3" gutterBottom>
                                {t('OFFER_REQUESTS.DETAIL.MORE_INFORMATION')}
                            </Typography>
                            <Stack spacing={2}>
                                {offerRequest.moreInformation.map((infoField, index) => (
                                    <Field
                                        key={`${pickLocalizedText(infoField.question, i18n.language)}_${index}`}
                                        label={pickLocalizedText(infoField.question, i18n.language)}
                                        value={pickLocalizedText(infoField.answer, i18n.language)}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {offerRequest.attachments.length > 0 && (
                        <>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle1" component="h3" gutterBottom>
                                    {t('OFFER_REQUESTS.DETAIL.ATTACHMENTS')}
                                </Typography>
                                <Stack spacing={2}>
                                    {documentAttachments.length > 0 && (
                                        <Stack spacing={1}>
                                            {documentAttachments.map((attachment, index) => (
                                                <Link
                                                    key={attachment.uuid}
                                                    href={attachment.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="body2"
                                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                    <InsertDriveFileOutlinedIcon fontSize="small" />
                                                    {attachment.displayName ||
                                                        t('OFFER_REQUESTS.DETAIL.ATTACHMENT_ALT', {
                                                            index: index + 1,
                                                        })}
                                                </Link>
                                            ))}
                                        </Stack>
                                    )}

                                    {imageAttachments.length > 0 && (
                                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                            {imageAttachments.map((attachment, index) => {
                                                const label =
                                                    attachment.displayName ||
                                                    t('OFFER_REQUESTS.DETAIL.ATTACHMENT_ALT', { index: index + 1 });
                                                return (
                                                    <Box
                                                        key={attachment.uuid}
                                                        component="a"
                                                        href={attachment.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={label}
                                                        sx={{
                                                            display: 'inline-flex',
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                        }}>
                                                        <Box
                                                            component="img"
                                                            src={attachment.url}
                                                            alt={label}
                                                            sx={{
                                                                width: 128,
                                                                height: 128,
                                                                objectFit: 'cover',
                                                                borderRadius: 1,
                                                                bgcolor: 'action.hover',
                                                                display: 'block',
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
